import * as React from 'react';
import type { FC, PropsWithChildren, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { when } from '@legendapp/state';
import { use$, useObservable } from '@legendapp/state/react';
import { useDrag } from '@use-gesture/react';
import { Breakpoints, styled, useTheme } from '@volkswagen-onehub/components-core';
import { ChevronLeft, ChevronRight } from '@volkswagen-onehub/icons-core';
import { animated, useSpring } from 'react-spring';
import { css } from 'styled-components';

import { landscapeMediaMixin } from '../../hooks/use-vertical-breakpoint';

import translate from '../../utils/translate';

import { fullscreenStore } from '../../store';

interface SliderProps {
	parentRef: RefObject<HTMLDivElement>;
	activeOptionOrPickerRef?: RefObject<HTMLButtonElement>;
	showArrows?: boolean;
}

const START_POINT = 16; // Used to move swatches from start 0 right 16px
const EDGE_EPSILON = 1;

const Slider: FC<PropsWithChildren<SliderProps>> = ({
	children,
	parentRef,
	activeOptionOrPickerRef,
	showArrows = false
}) => {
	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);
	const { direction } = useTheme();

	const isLTR = direction == 'ltr';

	const sliderRef = useRef<HTMLDivElement>(null);
	const scrollAnimRef = useRef<number | null>(null);

	const fromX = useObservable(0);
	const scrollPos = useObservable(0);

	// Why useState? because of observable rendering within useEffect / resize
	const [parentWidth, setParentWidth] = useState(0);
	const [sliderWidth, setSliderWidth] = useState(0);

	const widthDelta = sliderWidth - parentWidth;
	const isOptionsFit = widthDelta <= START_POINT;
	const boundaries = {
		left: isOptionsFit ? 0 : isLTR ? -widthDelta : 0,
		right: isOptionsFit ? 0 : isLTR ? 0 : widthDelta
	};

	// TODO: possible switch this? right now useEffect is the most reliable method
	useEffect(() => {
		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				if (entry.target === parentRef.current) {
					const newWidth = entry.contentRect.width;
					if (newWidth !== parentWidth) {
						setParentWidth(newWidth);
					}
				}

				if (entry.target === sliderRef.current) {
					const newWidth = entry.contentRect.width;
					if (newWidth !== sliderWidth) {
						setSliderWidth(newWidth);
					}
				}
			}
		});

		if (parentRef.current) observer.observe(parentRef.current);
		if (sliderRef.current) observer.observe(sliderRef.current);
		if (activeOptionOrPickerRef?.current) observer.observe(activeOptionOrPickerRef.current);

		const handleResizeOrOrientationChange = () => {
			moveToActivePicker();
		};

		window.addEventListener('resize', handleResizeOrOrientationChange);
		window.addEventListener('orientationchange', handleResizeOrOrientationChange);

		return () => {
			window.removeEventListener('resize', handleResizeOrOrientationChange);
			window.removeEventListener('orientationchange', handleResizeOrOrientationChange);
			observer.disconnect();
		};
	}, [parentRef, sliderRef, activeOptionOrPickerRef]);

	useEffect(() => () => cancelScrollAnimation(), []);

	const [{ x }, api] = useSpring(() => ({ x: 0 }));

	useDrag(
		({ offset: [offsetX] }) => {
			cancelScrollAnimation();
			fromX.set(offsetX);
			scrollPos.set(offsetX);
			api.start({ x: offsetX, immediate: true });
		},
		{
			target: sliderRef,
			pointer: { touch: true, mouse: true },
			from: () => [fromX.get(), 0],
			bounds: () => boundaries,
			axis: 'x',
			enabled: parentWidth > 0 && sliderWidth > 0 && !isOptionsFit
		}
	);

	useEffect(() => {
		moveToActivePicker();
	}, [activeOptionOrPickerRef, parentWidth, sliderWidth]);

	when(
		() => isOptionsFit && x.get() !== 0,
		() => {
			fromX.set(0);
			scrollPos.set(0);
			api.start({ x: 0, immediate: true });
		}
	);

	function cancelScrollAnimation() {
		if (scrollAnimRef.current !== null) {
			cancelAnimationFrame(scrollAnimRef.current);
			scrollAnimRef.current = null;
		}
	}

	function moveToActivePicker() {
		if (!parentRef.current || !sliderRef.current || !activeOptionOrPickerRef?.current || isOptionsFit) return;

		const parentWidth = parentRef.current.offsetWidth;
		const sliderWidth = sliderRef.current.offsetWidth;

		const activeOffsetLeft = activeOptionOrPickerRef.current.offsetLeft;

		if (parentWidth === 0 || sliderWidth === 0) return;

		const boundaries = {
			left: isLTR ? -widthDelta : 0,
			right: isLTR ? 0 : widthDelta
		};

		let targetPosition;

		if (isLTR) {
			targetPosition = START_POINT - activeOffsetLeft;
		} else {
			// RTL - no offsetWidth exists :'( what were MDN people thinking?
			const activeItemWidth = activeOptionOrPickerRef.current.offsetWidth;
			const activeOffsetRight = sliderWidth - (activeOffsetLeft + activeItemWidth);
			targetPosition = activeOffsetRight - START_POINT;
		}

		const newX = Math.max(boundaries.left, Math.min(boundaries.right, targetPosition));

		cancelScrollAnimation();
		fromX.set(newX);
		scrollPos.set(newX);
		api.start({ x: newX, immediate: true });
	}

	function scrollByPage(towardsStart: boolean) {
		if (parentWidth === 0) return;
		const page = parentWidth * 0.8;
		const delta = towardsStart ? page : -page;
		const target = Math.max(boundaries.left, Math.min(boundaries.right, scrollPos.get() + delta));

		animateScrollTo(target);
	}

	function animateScrollTo(target: number) {
		cancelScrollAnimation();

		const start = fromX.get();
		const startTime = performance.now();
		const duration = 300;
		const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

		scrollPos.set(target);

		const tick = (now: number) => {
			const t = Math.min(1, (now - startTime) / duration);
			const value = start + (target - start) * easeOut(t);

			fromX.set(value);
			api.start({ x: value, immediate: true });

			scrollAnimRef.current = t < 1 ? requestAnimationFrame(tick) : null;
		};

		scrollAnimRef.current = requestAnimationFrame(tick);
	}

	const currentX = use$(scrollPos);
	const showStartArrow = showArrows && !isOptionsFit && currentX < boundaries.right - EDGE_EPSILON;
	const showEndArrow = showArrows && !isOptionsFit && currentX > boundaries.left + EDGE_EPSILON;

	const previousLabel = translate('global.ariaLabel.scrollOptionsPrevious');
	const nextLabel = translate('global.ariaLabel.scrollOptionsNext');

	const inertWhenHidden = (visible: boolean) => (visible ? {} : ({ inert: '' } as Record<string, string>));

	return (
		<Wrapper isFullscreenMode={isFullscreenMode}>
			<ArrowButton
				type="button"
				$edge="start"
				$visible={showStartArrow}
				{...inertWhenHidden(showStartArrow)}
				aria-label={isLTR ? previousLabel : nextLabel}
				onClick={e => {
					e.stopPropagation();
					scrollByPage(true);
				}}
			>
				<ChevronLeft aria-hidden variant="small" />
			</ArrowButton>
			<animated.div
				ref={sliderRef}
				style={{
					x,
					touchAction: 'none',
					display: 'inline-flex',
					userSelect: 'none',
					willChange: 'transform'
				}}
			>
				<ItemContainer
					isFullscreenMode={isFullscreenMode}
					isOptionsFit={isOptionsFit}
					onClick={e => e.stopPropagation()}
				>
					{children}
				</ItemContainer>
			</animated.div>
			<ArrowButton
				type="button"
				$edge="end"
				$visible={showEndArrow}
				{...inertWhenHidden(showEndArrow)}
				aria-label={isLTR ? nextLabel : previousLabel}
				onClick={e => {
					e.stopPropagation();
					scrollByPage(false);
				}}
			>
				<ChevronRight aria-hidden variant="small" />
			</ArrowButton>
		</Wrapper>
	);
};

export default Slider;

const Wrapper = styled.div<{ isFullscreenMode: boolean }>`
	position: relative;
	overflow: hidden;

	// Stops checkmark from being cutoff
	padding-top: ${({ theme }) => theme.size.static150};
	// Bottom is set here for control panel as we require the padding for a11y outline
	padding-bottom: ${({ theme }) => theme.size.static150};

	${props => props.isFullscreenMode && DoubleSliderMixin()} @media ${landscapeMediaMixin} {
		${() => DoubleSliderMixin()}
	}
`;

const ArrowButton = styled.button<{ $edge: 'start' | 'end'; $visible: boolean }>`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	${({ $edge, theme }) =>
		$edge === 'start' ? `inset-inline-start: ${theme.size.static150};` : `inset-inline-end: ${theme.size.static150};`}
	z-index: 2;

	display: flex;
	align-items: center;
	justify-content: center;

	width: 32px;
	height: 32px;
	padding: 0;
	border: none;
	border-radius: 50%;
	cursor: pointer;

	background: #ffffff;
	color: #001e50;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);

	& svg {
		width: 12px;
		height: 12px;
	}

	opacity: ${({ $visible }) => ($visible ? 1 : 0)};
	pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
	transition: opacity 150ms ease;
`;

const DoubleSliderMixin = () => {
	const masking = css`
		mask-image: linear-gradient(
			to right,
			transparent 0px,
			black ${({ theme }) => theme.size.static150},
			black calc(100% - ${({ theme }) => theme.size.static150}),
			transparent 100%
		);
	`;

	return css`
		${masking}
	`;
};

const ItemContainer = styled.div<{ isOptionsFit: boolean; isFullscreenMode: boolean }>`
	// Prevents chips breaking to next line
	display: flex;
	flex-wrap: nowrap;

	${({ theme, isOptionsFit, isFullscreenMode }) => {
		const isLTR = theme.direction === 'ltr';
		const childMainMargin = isLTR ? 'margin-right' : 'margin-left';
		const oppositeMargin = isLTR ? 'margin-left' : 'margin-right';

		const commonStyle = css`
			& > div,
			& > button {
				${childMainMargin}: ${props => props.theme.size.static150};
			}

			& > div:first-child:not(:only-child),
			& > button:first-child:not(:only-child) {
				${oppositeMargin}: ${theme.size.static250};

				@media ${landscapeMediaMixin} {
					margin-left: ${({ theme }) => (isLTR ? theme.size.static150 : theme.size.static200)};
					margin-right: ${({ theme }) => (isLTR ? theme.size.static200 : theme.size.static150)};
				}
			}

			// Last chip is shaded, we want to push it out of the shading

			& > div:last-child:not(:only-child),
			& > button:last-child:not(:only-child) {
				${isOptionsFit ? `${childMainMargin}: ${theme.size.static250}` : `${childMainMargin}: ${theme.size.static350}`};

				@media ${landscapeMediaMixin} {
					margin-left: ${({ theme }) => (isLTR ? '0' : theme.size.static350)};
					margin-right: ${({ theme }) => (isLTR ? theme.size.static350 : '0')};
				}
			}

			// If only one child we need equal margins

			& > div:only-child,
			& > button:only-child {
				margin-left: ${({ theme }) => theme.size.static350};
				margin-right: ${({ theme }) => theme.size.static350};
			}
		`;

		if (isFullscreenMode) {
			return commonStyle;
		}

		return css`
			${commonStyle};

			@media (min-width: ${Breakpoints.b960}px) {
				& > div:first-child:not(:only-child),
				& > button:first-child:not(:only-child) {
					// If options fit, we want the same margin as is at top of this styled component
					${oppositeMargin}: ${isOptionsFit ? theme.size.static150 : theme.size.static250};
				}
			}
		`;
	}}
`;
